<template>
  <div class="dashboard-layout h-screen flex flex-col">
    <Header 
      title="Dashboard" 
      :showSearch="true" 
      :showMenuToggle="true" 
      @toggle-sidebar="toggleSidebar"
      @search="handleSearch"
    />
    
    <div class="flex flex-1 overflow-hidden">
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-6 transition-all duration-300"
      >
        <LoadingSpinner :loading="loading" message="Loading dashboard..." />
        <Toast ref="toast" />

        <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span class="block sm:inline">{{ error }}</span>
        </div>

        <!-- Dashboard Content -->
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
          
          <!-- Stats Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-gray-500 text-sm">Total Properties</p>
                  <h3 class="text-2xl font-bold mt-1">{{ propertyStats.total }}</h3>
                </div>
                <div class="bg-primary-100 p-3 rounded-full">
                  <i class="fas fa-building text-primary-600"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-gray-500 text-sm">Paid Properties</p>
                  <h3 class="text-2xl font-bold mt-1">{{ propertyStats.paid }}</h3>
                </div>
                <div class="bg-green-100 p-3 rounded-full">
                  <i class="fas fa-check-circle text-green-600"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-gray-500 text-sm">Unpaid Properties</p>
                  <h3 class="text-2xl font-bold mt-1">{{ propertyStats.unpaid }}</h3>
                </div>
                <div class="bg-red-100 p-3 rounded-full">
                  <i class="fas fa-exclamation-circle text-red-600"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-gray-500 text-sm">Total Value</p>
                  <h3 class="text-2xl font-bold mt-1">${{ totalValueFormatted }}</h3>
                </div>
                <div class="bg-yellow-100 p-3 rounded-full">
                  <i class="fas fa-dollar-sign text-yellow-600"></i>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Map -->
          <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
            <h3 class="text-lg font-medium mb-4">Property Map</h3>
            <div class="h-[400px]">
              <MapContainer />
            </div>
          </div>
          
          <!-- Recent Properties and Notifications -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Recent Properties -->
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium">Recent Properties</h3>
                <router-link 
                  to="/properties" 
                  class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View All
                </router-link>
              </div>
              
              <div v-if="propertiesStore.loading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin text-primary-600"></i>
              </div>
              <div v-else-if="propertiesStore.properties.length === 0" class="text-center py-4 text-gray-500">
                No properties found
              </div>
              <div v-else>
                <div 
                  v-for="property in recentProperties" 
                  :key="property.id" 
                  class="border-b last:border-0 py-3"
                >
                  <div class="flex justify-between items-start">
                    <div>
                      <h4 class="font-medium">{{ property.title }}</h4>
                      <p class="text-sm text-gray-500">{{ property.address }}</p>
                    </div>
                    <span 
                      :class="{
                        'bg-green-100 text-green-800': property.payment_status === 'paid',
                        'bg-red-100 text-red-800': property.payment_status === 'unpaid'
                      }"
                      class="px-2 py-1 rounded-full text-xs"
                    >
                      {{ property.payment_status }}
                    </span>
                  </div>
                  <div class="mt-2 flex justify-between items-center">
                    <p class="font-medium">${{ property.price.toLocaleString() }}</p>
                    <button 
                      @click="viewPropertyDetails(property.id)" 
                      class="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Notifications -->
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium">Notifications</h3>
                <button class="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Mark All Read
                </button>
              </div>
              
              <div v-if="notifications.length === 0" class="text-center py-4 text-gray-500">
                No notifications
              </div>
              <div v-else>
                <div 
                  v-for="(notification, index) in notifications" 
                  :key="index" 
                  class="border-b last:border-0 py-3 flex items-start"
                >
                  <div 
                    :class="{
                      'bg-red-100 text-red-600': notification.type === 'payment',
                      'bg-blue-100 text-blue-600': notification.type === 'info',
                      'bg-green-100 text-green-600': notification.type === 'success'
                    }"
                    class="p-2 rounded-full mr-3"
                  >
                    <i 
                      :class="{
                        'fas fa-exclamation-circle': notification.type === 'payment',
                        'fas fa-info-circle': notification.type === 'info',
                        'fas fa-check-circle': notification.type === 'success'
                      }"
                    ></i>
                  </div>
                  <div class="flex-1">
                    <h4 class="font-medium">{{ notification.title }}</h4>
                    <p class="text-sm text-gray-500">{{ notification.message }}</p>
                    <p class="text-xs text-gray-400 mt-1">{{ notification.time }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import Header from '../components/Header.vue';
import Sidebar from '../components/Sidebar.vue';
import MapContainer from '../components/MapContainer.vue';
import { usePropertiesStore } from '../stores/properties';
import { useRouter } from 'vue-router';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import Toast from '../components/Toast.vue';

const router = useRouter();
const propertiesStore = usePropertiesStore();
const sidebarCollapsed = ref(false);
const loading = ref(true);
const error = ref(null);
const toast = inject('toast');

// Notifications - sample data
const notifications = [
  {
    type: 'payment',
    title: 'Payment Overdue',
    message: '3 properties have overdue payments',
    time: '2 hours ago'
  },
  {
    type: 'info',
    title: 'New Property Added',
    message: 'Downtown Apartment has been added to your portfolio',
    time: '1 day ago'
  },
  {
    type: 'success',
    title: 'Payment Received',
    message: 'Payment for Riverside Villa has been processed',
    time: '3 days ago'
  }
];

// Computed properties
const propertyStats = computed(() => {
  return propertiesStore.propertyStatistics;
});

const totalValue = computed(() => {
  return propertiesStore.properties.reduce((sum, property) => sum + property.price, 0);
});

const totalValueFormatted = computed(() => {
  if (totalValue.value >= 1000000) {
    return `${(totalValue.value / 1000000).toFixed(2)}M`;
  } else if (totalValue.value >= 1000) {
    return `${(totalValue.value / 1000).toFixed(2)}K`;
  } else {
    return totalValue.value.toLocaleString();
  }
});

const recentProperties = computed(() => {
  return [...propertiesStore.properties]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);
});

// Methods
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

const handleSearch = (query) => {
  console.log('Search query:', query);
  // Implement search functionality
};

const viewPropertyDetails = (id) => {
  // Navigate to property details page
  router.push(`/properties/${id}`);
};

const fetchDashboardData = async () => {
  loading.value = true;
  error.value = null;
  try {
    await propertiesStore.fetchProperties();
    toast.value?.addToast('Dashboard data updated', 'success');
  } catch (err) {
    error.value = err.message || 'Failed to load dashboard data';
    toast.value?.addToast(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

const refreshData = async () => {
  await fetchDashboardData();
};

onMounted(() => {
  fetchDashboardData();
});
</script>